import { ReactNode, createContext, useContext, useMemo, useState } from "react";
import LocationPageModal from "../components/LocationPageModal";

interface QueryObject {
  query?: string;
  latitude?: number;
  longitude?: number;
  radius?: number;
  fsa?: string;
}

export interface AddressObject {
  street1: string;
  street2: string;
  city: string;
  province: string;
  postal_code: string;
}

export interface PhoneNumberObject {
  description: string;
  extension: string;
  name: string;
  number: string;
  type: string;
}

export interface ServiceObject {
  address: AddressObject;
  description: string;
  id: string;
  last_updated: string;
  latitude: number;
  longitude: number;
  metadata: object;
  name: string;
  phone_numbers: Array<PhoneNumberObject>;
}

interface ServicesObject {
  is_emergency: boolean;
  is_out_of_scope: boolean;
  message: string;
  no_services_found: boolean;
  services: Array<ServiceObject>;
}

interface QuestionsObject {
  query?: QueryObject;
  questions?: Array<string>;
  answers?: Array<string>;
  recommendation?: string;
}

interface LocationObject {
  latitude?: number;
  longitude?: number;
  radius?: string;
  fsa?: string;
  postalCode?: string;
  city?: string;
  province?: string;
  formattedAddress?: string;
}

export interface RecoEngineContextType {
  queryObject?: QueryObject;
  setQueryObject?: (value: QueryObject) => void;
  loading: boolean;
  setLoading: (value: boolean) => void;
  servicesObject?: ServicesObject;
  setServicesObject?: (value: ServicesObject) => void;
  renderBlocks?: Array<any>;
  setRenderBlocks?: (value: any) => void;
  feedbackRow: boolean;
  setFeedbackRow: (value: boolean) => void;
  readyForQuery: boolean;
  setReadyForQuery: (value: boolean) => void;
  showLocationModal: boolean;
  setShowLocationModal: (value: boolean) => void;
  questionsObject?: QuestionsObject;
  setQuestionsObject?: (value: QuestionsObject) => void;
  refineForm?: boolean;
  setRefineForm?: (value: boolean) => void;
  locationData?: LocationObject;
  setLocationData?: (value: LocationObject) => void;
  refineFormCount?: number;
  setRefineFormCount?: (value: number) => void;
}

// Create Context
const RecoEngineContext = createContext<RecoEngineContextType| undefined>(undefined);

interface RecoEngineProviderProps {
  children: ReactNode;
}

// Reco Engine Context Provider
export const RecoEngineProvider = ({ children }: RecoEngineProviderProps) => {
    const [queryObject, setQueryObject] = useState<QueryObject>({});
    const [servicesObject, setServicesObject] = useState<ServicesObject | undefined>(undefined);
    const [questionsObject, setQuestionsObject] = useState<QuestionsObject | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [feedbackRow, setFeedbackRow] = useState(false);
    const [readyForQuery, setReadyForQuery] = useState(false);
    const [refineForm, setRefineForm] = useState(false);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [renderBlocks, setRenderBlocks] = useState([]);
    const [locationData, setLocationData] = useState<LocationObject>(() => {
      const savedLocation = localStorage.getItem("location");
      return savedLocation ? JSON.parse(savedLocation) : {};
    });
    const [refineFormCount, setRefineFormCount] = useState<number>(0);

    const value = useMemo(
      () => ({
        queryObject,
        setQueryObject,
        loading,
        setLoading,
        servicesObject,
        setServicesObject,
        renderBlocks,
        setRenderBlocks,
        feedbackRow,
        setFeedbackRow,
        readyForQuery,
        setReadyForQuery,
        questionsObject,
        setQuestionsObject,
        refineForm,
        setRefineForm,
        locationData,
        setLocationData,
        showLocationModal,
        setShowLocationModal,
        refineFormCount,
        setRefineFormCount,
      }),
      [
        queryObject, 
        loading, 
        servicesObject, 
        renderBlocks, 
        feedbackRow, 
        readyForQuery, 
        questionsObject, 
        refineForm,
        locationData,
        showLocationModal,
        refineFormCount,
      ]
    );
    
    return (
        <RecoEngineContext.Provider value={value}>
          <LocationPageModal />
          {children}
        </RecoEngineContext.Provider>
    );
}

// hook to use Reco Engine Context
export const useRecoEngine = () => {
  const context = useContext(RecoEngineContext);
  if (!context) {
    throw new Error("useRecoEngine must be used within a RecoEngineProvider");
  }
  return context;
};